/**
 * Expenses Page
 * Refactored main component - uses sub-components from ./expenses/
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Receipt, Plus } from 'lucide-react';
import { useTaxYear } from '../context/TaxYearContext';
import { useSubscription } from '../context/SubscriptionContext';

import {
  ExpenseSummaryCards,
  ExpenseFilters,
  ExpenseCard,
  ExpenseDialog,
  DeleteConfirmDialog
} from './expenses';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getDefaultFormData = () => ({
  date: new Date().toISOString().split('T')[0],
  category: 'travel',
  description: '',
  amount: '',
  reimbursement_status: 'not_claimed',
  is_tax_deductible: true,
  document_id: null,
  trip_id: null,
  notes: ''
});

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const { selectedYear } = useTaxYear();
  const { canAddContent } = useSubscription();
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState(getDefaultFormData());
  const [savingExpense, setSavingExpense] = useState(false);
  const [taxYears, setTaxYears] = useState([]);

  const fetchTaxYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/tax-years`);
      setTaxYears(response.data.tax_years);
    } catch (error) {
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedYear && selectedYear !== 'all') {
        const startYear = selectedYear.split('/')[0];
        params.append('tax_year', startYear);
      }
      if (monthFilter !== 'all' && yearFilter !== 'all') {
        params.append('month', `${yearFilter}-${monthFilter}`);
      }
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter !== 'all') params.append('reimbursement_status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/expenses?${params.toString()}`);
      
      setExpenses(response.data.expenses || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, categoryFilter, statusFilter, monthFilter, yearFilter]);

  useEffect(() => {
    fetchTaxYears();
  }, [fetchTaxYears]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        date: expense.date || '',
        category: expense.category || 'travel',
        description: expense.description || '',
        amount: expense.amount?.toString() || '',
        reimbursement_status: expense.reimbursement_status || 'not_claimed',
        is_tax_deductible: expense.is_tax_deductible ?? true,
        document_id: expense.document_id || null,
        trip_id: expense.trip_id || null,
        notes: expense.notes || ''
      });
    } else {
      setEditingExpense(null);
      setFormData(getDefaultFormData());
    }
    setDialogOpen(true);
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    setSavingExpense(true);
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0
      };
      
      if (editingExpense) {
        await axios.put(`${API_URL}/api/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload);
        toast.success('Expense added successfully');
      }
      
      setDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/expenses/${expenseId}`);
      toast.success('Expense deleted');
      setDeleteConfirmOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirm = (expense) => {
    setExpenseToDelete(expense);
    setDeleteConfirmOpen(true);
  };

  const availableYears = useMemo(() =>
    taxYears.map(ty => {
      const parts = ty.split('/');
      return [parts[0], parts[1] || String(parseInt(parts[0]) + 1)];
    }).flat().filter((v, i, a) => a.indexOf(v) === i).sort().reverse()
  , [taxYears]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Expenses</h1>
          <p className="text-slate-400">Track work-related expenses and reimbursements</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => handleOpenDialog()} className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50" data-testid="add-expense-btn" disabled={!canAddContent}>
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <ExpenseSummaryCards summary={summary} />

      {/* Filters */}
      <ExpenseFilters
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        monthFilter={monthFilter}
        setMonthFilter={setMonthFilter}
        yearFilter={yearFilter}
        setYearFilter={setYearFilter}
        availableYears={availableYears}
      />

      {/* Expenses List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>
        ) : expenses.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-12 text-center">
            <Receipt className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No expenses found</h3>
            <p className="text-slate-400 mb-4">Start tracking your work-related expenses</p>
            <Button onClick={() => handleOpenDialog()} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="h-4 w-4 mr-2" />
              Add First Expense
            </Button>
          </div>
        ) : (
          expenses.map(expense => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              isExpanded={expandedId === expense.id}
              onToggle={() => setExpandedId(expandedId === expense.id ? null : expense.id)}
              onEdit={handleOpenDialog}
              onDelete={openDeleteConfirm}
            />
          ))
        )}
      </div>

      {/* Add/Edit Expense Dialog */}
      <ExpenseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingExpense={editingExpense}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSaveExpense}
        saving={savingExpense}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setExpenseToDelete(null);
        }}
        expense={expenseToDelete}
        onDelete={handleDeleteExpense}
        deleting={deleting}
      />
    </div>
  );
};

export default Expenses;
